import { getTemplateObjectRecords } from './designerObjectIds.js';

/**
 * Validate the object tree stored by one template page.
 *
 * The template page itself is not a runtime object. Its exported children
 * therefore form one rooted forest candidate, connected by `parentid`.
 * Nothing is repaired here: a malformed template is reported so the user can
 * fix the Designer hierarchy without silently changing authored objects.
 */
export function validateTemplateTree(templatePage, { records = null } = {}) {
    const objects = records ?? getTemplateObjectRecords(templatePage);
    const errors = [];
    const byId = new Map();

    for (const record of objects) {
        const id = Number(record?.id);
        if (!Number.isInteger(id)) {
            errors.push('Template contains an object without a valid ID');
            continue;
        }
        if (byId.has(id)) errors.push(`Template contains duplicate object ID ${id}`);
        byId.set(id, record);
    }

    const hasParent = record => record?.parentid !== undefined &&
        record?.parentid !== null && record?.parentid !== '';
    const roots = objects.filter(record => !hasParent(record));

    if (roots.length !== 1) {
        errors.push(`Template must contain exactly one root object (found ${roots.length})`);
    }

    for (const record of objects) {
        if (!hasParent(record)) continue;
        const parentId = Number(record.parentid);
        if (!Number.isInteger(parentId) || !byId.has(parentId)) {
            errors.push(`Template object ${record.id} has an orphan parent ${record.parentid}`);
        }
    }

    const state = new Map();
    const visit = id => {
        const current = state.get(id) ?? 0;
        if (current === 1) {
            errors.push(`Template object tree contains a cycle at object ${id}`);
            return;
        }
        if (current === 2) return;
        state.set(id, 1);
        const record = byId.get(id);
        if (record && hasParent(record)) {
            const parentId = Number(record.parentid);
            if (byId.has(parentId)) visit(parentId);
        }
        state.set(id, 2);
    };
    for (const id of byId.keys()) visit(id);

    return {
        valid: errors.length === 0,
        errors: [...new Set(errors)],
        rootIds: roots.map(record => Number(record.id)),
        records: objects,
    };
}

/** Validate every template page in a Designer layer. */
export function validateDesignerTemplateTrees(layer) {
    return (layer?.getChildren?.() ?? [])
        .filter(page => page?.obj === 'template_page')
        .map(template => ({ template, ...validateTemplateTree(template) }))
        .filter(result => !result.valid);
}
